import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { Box, Button, Typography, IconButton, TextField } from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faToggleOn, faToggleOff } from '@fortawesome/free-solid-svg-icons';
import CustomTable from '@src/components/CustomTable';
import Popup from '@src/components/Popup';
import CreditManagementApi from '@src/apis/creditManagement';
import { COLOR } from '@src/styles/color';
import { INIT_PAGINATION } from '@src/constants';
import CreateCreditModal from './CreateCreditModal';
import DetailCreditModal from './DetailCreditModal';
import { StyledCreditManagement } from './index.style';

const CreditManagement = ({ appId }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [creditPackages, setCreditPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(INIT_PAGINATION);

  const [searchTerm, setSearchTerm] = useState('');

  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const [selectedCreditPackage, setSelectedCreditPackage] = useState(null);
  const [deleteCreditPackage, setDeleteCreditPackage] = useState(null);

  const fetchCreditPackages = async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: pagination.limit,
        ...(appId && { appId }),
        ...(searchTerm && { searchTerm }),
      };

      const response = await CreditManagementApi.getCreditPackages(params);

      if (response?.items) {
        setCreditPackages(response.items || []);
        setPagination({
          ...pagination,
          page: response.pagination?.currentPage || page,
          total: response.pagination?.total || 0,
          totalPages: response.pagination?.pagesCount || 0,
        });
      }
    } catch (error) {
      enqueueSnackbar(t('fetchCreditPackagesError'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination({ ...pagination, page: 1 });
    fetchCreditPackages(1);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handlePageChange = (newPage) => {
    fetchCreditPackages(newPage);
  };

  const handleToggleStatus = async (creditPackage) => {
    try {
      const newStatus = !creditPackage.isActive;
      const response = await CreditManagementApi.updateCreditPackageStatus(
        creditPackage.id,
        { isActive: newStatus },
      );

      if (response?.status) {
        enqueueSnackbar(
          newStatus
            ? t('creditPackageActivated')
            : t('creditPackageDeactivated'),
          { variant: 'success' },
        );
        fetchCreditPackages(pagination.page);
      }
    } catch (error) {
      enqueueSnackbar(t('updateStatusError'), { variant: 'error' });
    }
  };

  const handleEdit = (creditPackage) => {
    setSelectedCreditPackage(creditPackage);
    setOpenDetailModal(true);
  };

  const handleDelete = (creditPackage) => {
    setDeleteCreditPackage(creditPackage);
    setOpenDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const response = await CreditManagementApi.deleteCreditPackage(
        deleteCreditPackage.id,
      );
      if (response?.status) {
        enqueueSnackbar(t('deleteCreditPackageSuccess'), {
          variant: 'success',
        });
        setOpenDeleteModal(false);
        fetchCreditPackages(pagination.page);
      }
    } catch (error) {
      enqueueSnackbar(t('deleteCreditPackageError'), { variant: 'error' });
    }
  };

  const handleModalClose = () => {
    setOpenCreateModal(false);
    setOpenDetailModal(false);
    setSelectedCreditPackage(null);
  };

  const handleSuccess = () => {
    fetchCreditPackages(pagination.page);
    handleModalClose();
  };

  useEffect(() => {
    fetchCreditPackages();
  }, []);

  const heads = [
    {
      label: t('id'),
      valueName: 'id',
      align: 'left',
    },
    {
      label: t('packageName'),
      valueName: 'packageName',
      align: 'left',
    },
    {
      label: t('toolName'),
      valueName: 'toolName',
      align: 'left',
    },
    {
      label: t('creditValue'),
      valueName: 'creditValue',
      align: 'center',
    },
    {
      label: t('status'),
      valueName: 'status',
      align: 'center',
    },
    {
      label: t('actions'),
      valueName: 'action',
      align: 'center',
    },
  ];

  const formatData = creditPackages.map((creditPackage) => ({
    ...creditPackage,
    id: creditPackage.id?.substring(0, 8) || '--',
    packageName: creditPackage.displayName,
    toolName:
      creditPackage.tool?.translations?.vi?.name ||
      creditPackage.tool?.translations?.en?.name ||
      creditPackage.tool?.name?.vi ||
      creditPackage.tool?.name?.en ||
      '-',
    creditValue: (
      <Typography className="credit-value">
        {creditPackage.creditValue}
      </Typography>
    ),
    status: (
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          handleToggleStatus(creditPackage);
        }}
      >
        <FontAwesomeIcon
          icon={creditPackage.isActive ? faToggleOn : faToggleOff}
          color={
            creditPackage.isActive ? COLOR.success.base : COLOR.blueGray[64]
          }
          size="lg"
        />
      </IconButton>
    ),
    action: (
      <Box display="flex" gap={1} justifyContent="center">
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleEdit(creditPackage);
          }}
        >
          <Edit color="primary" fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleDelete(creditPackage);
          }}
        >
          <Delete color="error" fontSize="small" />
        </IconButton>
      </Box>
    ),
  }));

  return (
    <StyledCreditManagement>
      <Box className="header-card">
        <Box className="header-card-title">
          <Typography variant="h3">{t('creditManagement')}</Typography>
          <Button
            variant="contained"
            onClick={() => setOpenCreateModal(true)}
            startIcon={<Add />}
          >
            {t('createCreditPackage')}
          </Button>
        </Box>

        <Box className="filters" display="flex">
          <Box display="flex" flex={1} gap={4}>
            <TextField
              className="search-input"
              size="small"
              placeholder={t('searchCreditPackages')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleKeyPress}
            />
          </Box>
          <Button variant="outlined" onClick={handleSearch}>
            {t('search')}
          </Button>
        </Box>
      </Box>

      <Box className="body-card">
        <CustomTable
          actions={[]}
          items={formatData}
          heads={heads}
          loading={loading}
          pagination={pagination}
          onChangePagination={handlePageChange}
          disableRowClick
        />
      </Box>

      <CreateCreditModal
        open={openCreateModal}
        onClose={handleModalClose}
        onSuccess={handleSuccess}
        appId={appId}
      />

      <DetailCreditModal
        open={openDetailModal}
        creditPackage={selectedCreditPackage}
        onClose={handleModalClose}
        onSuccess={handleSuccess}
        appId={appId}
      />

      <Popup
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        title={t('confirmDeleteCreditPackage')}
        content={t('deleteCreditPackageConfirm', {
          name: deleteCreditPackage?.displayName || deleteCreditPackage?.id,
        })}
        okMessage={t('yes')}
        cancelMessage={t('no')}
        onOk={confirmDelete}
        titleColor={COLOR.warning.base}
      />
    </StyledCreditManagement>
  );
};

export default CreditManagement;
